import { useEffect } from "react";
import axios from "axios";
import { invalidateApiCache } from "@/lib/apiCache";
import { API } from "@/apiConfig";

// Global forced-logout: after Day Close (manual or automatic), every terminal's token is
// rejected on its next request — clear auth and drop straight to the login screen instead
// of showing a raw error. Runs once per module load (registered on the shared axios
// instance every file's `import axios from "axios"` uses).
export function useForcedLogoutInterceptor() {
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // POS pages only — the mobile app, Console and Backoffice Web have their own logins and
        // must never be thrown onto the register's login screen by an expired/failed token.
        const onPosPage = window.location.pathname.startsWith("/pos");
        // While the End of Day dialog is showing its result, its own token is already invalid
        // (Day Close logs everyone out) — let the dialog finish; it logs out on dismiss.
        const eodShowingResult = sessionStorage.getItem("eod_in_progress") === "1";
        if (error.response?.status === 401 && onPosPage && !eodShowingResult && window.location.pathname !== "/pos/login") {
          localStorage.removeItem("auth_session");
          localStorage.removeItem("token");
          invalidateApiCache();
          localStorage.removeItem("employee");
          window.location.href = "/pos/login?reason=logged_out";
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);
}

// Idle auto-logout (Settings › General › Register Options, default 60 min, 0 = off): any
// touch / key / mouse / scan counts as activity. Checked every 30 s on POS pages.
export function useIdleAutoLogout() {
  useEffect(() => {
    let lastActivity = Date.now();
    let idleMinutes = 60;
    const bump = () => { lastActivity = Date.now(); };
    const events = ["mousedown", "mousemove", "keydown", "touchstart", "wheel", "pointerdown"];
    events.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    const loadSetting = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const r = await axios.get(`${API}/settings/store`, { headers: { Authorization: `Bearer ${token}` } });
        const v = r.data?.idle_logout_minutes;
        idleMinutes = v === undefined || v === null ? 60 : Number(v);
      } catch {}
    };
    loadSetting();
    const settingTimer = setInterval(loadSetting, 5 * 60 * 1000);
    const idleTimer = setInterval(() => {
      const onPos = window.location.pathname.startsWith("/pos") && !["/pos/login", "/pos/customer-display"].includes(window.location.pathname);
      if (!onPos || !localStorage.getItem("token") || idleMinutes <= 0) return;
      if (Date.now() - lastActivity >= idleMinutes * 60 * 1000) {
        localStorage.removeItem("auth_session");
        localStorage.removeItem("token");
        localStorage.removeItem("employee");
        window.location.href = "/pos/login?reason=idle";
      }
    }, 30000);
    return () => { events.forEach((ev) => window.removeEventListener(ev, bump)); clearInterval(settingTimer); clearInterval(idleTimer); };
  }, []);
}

// Heartbeat: idle registers (nobody touching the screen) would otherwise stay "logged in"
// after Day Close until their next click. Every 15 s ask the backend whether this token is
// still valid — a 401 goes through the interceptor above and lands on the login screen.
export function useAuthHeartbeat() {
  useEffect(() => {
    const t = setInterval(() => {
      const onPos = window.location.pathname.startsWith("/pos") && window.location.pathname !== "/pos/login";
      const token = localStorage.getItem("token");
      if (!onPos || !token) return;
      axios.get(`${API}/employees/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, []);
}

// Unlock register when browser tab/window closes
export function useUnlockRegisterOnClose() {
  useEffect(() => {
    const handleBeforeUnload = () => {
      const stationId = localStorage.getItem("station_id");
      const fallbackToken = localStorage.getItem("token");
      if (stationId) {
        const url = `${API}/registers/unlock?station_id=${stationId}`;
        fetch(url, { method: "POST", headers: fallbackToken ? { Authorization: `Bearer ${fallbackToken}` } : {}, keepalive: true }).catch(() => {});
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);
}

// Lottery-Only mode (Distributor Console controlled): POS skips straight to Lottery
// on login and every other page is blocked. Public endpoint, safe to poll pre-login.
// Polls every 60s (not just once on mount) so toggling it in the Console takes effect
// on an already-running terminal without needing a restart — see V7.78 fix.
export function useLotteryOnlyMode(setLotteryOnly, setLotteryOnlyReady) {
  useEffect(() => {
    const poll = () => {
      fetch(`${API}/lottery/settings/public`)
        .then((r) => r.json())
        .then((data) => {
          const only = !!data?.lottery_only;
          setLotteryOnly(only);
          localStorage.setItem("lottery_only_cache", only ? "1" : "0");
        })
        .catch(() => {}) // keep whatever cached value was already seeded above
        .finally(() => setLotteryOnlyReady(true));
    };
    poll();
    const timer = setInterval(poll, 60000);
    return () => clearInterval(timer);
  }, [setLotteryOnly, setLotteryOnlyReady]);
}

// Console "Back Office" toggle for THIS terminal (Electron config, set from the license
// verify at app start): the terminal lands on Control Center instead of the sale screen.
export function useBackofficeTerminalConfig(setBackofficeTerminal) {
  useEffect(() => {
    const api = window.electronAPI;
    if (!api?.getConfig) return;
    api.getConfig().then((cfg) => {
      const on = !!cfg?.backoffice_mode;
      setBackofficeTerminal(on);
      localStorage.setItem("backoffice_terminal", on ? "1" : "0");
    }).catch(() => {});
  }, [setBackofficeTerminal]);
}
